import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import Toast, { ToastMessage, ToastType } from "./Toast";
import { subscribeToasts } from "../../services/toastBus";

interface ToastContextType {
  showToast: (type: ToastType, message: string, duration?: number) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, duration?: number) => {
      const id =
        Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const newToast: ToastMessage = {
        id,
        type,
        message,
        duration,
      };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Keep only the most recent toasts if we exceed maxToasts
        return updated.slice(-maxToasts);
      });

      return id;
    },
    [maxToasts]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Bridge non-React publishers (e.g. services/api.ts interceptor) into the toast UI. The bus is a tiny module-level pub/sub; see services/toastBus.ts.
  useEffect(() => {
    const unsubscribe = subscribeToasts(({ type, message, duration }) => {
      showToast(type, message, duration);
    });
    return unsubscribe;
  }, [showToast]);

  const contextValue: ToastContextType = {
    showToast,
    removeToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast container positioned at bottom right */}
      <div
        className="toast-container"
        style={{ position: "fixed", bottom: 0, right: 0, zIndex: 9999 }}
      >
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            style={{
              position: "absolute",
              bottom: `${index * 80}px`,
              right: 0,
            }}
          >
            <Toast toast={toast} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
